import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class ZettleSettingsReadOnly extends ModelObject<ZettleSettingsReadOnly> {

    public apiKeyLength: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.apiKeyLength = jsonSerializedObject.apiKeyLength;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.apiKeyLength = this.apiKeyLength;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "apiKeyLength", rule: (v: any) => v == null || v >= -2147483648 || "ApiKeyLength is not greater than minimum allowed value: -2147483648"},
            {field: "apiKeyLength", rule: (v: any) => v == null || v <= 2147483647 || "ApiKeyLength is above maximum allowed value: 2147483647"},
    ];
}


export class ZettleSettings extends ModelObject<ZettleSettings> {

    /**
    * The Api client id provided by the Zettle instance to be used for the link.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public apiClientId: string = "";
    private _defaultServiceChargeStockItemId: Guid | null = null;
    /**
    * Default Stock Item Id to be used for service charges on imported orders. This will be used if no BrewMan product name matches the service charge name in Zettle
    */
    public get defaultServiceChargeStockItemId(): Guid | null { return this._defaultServiceChargeStockItemId }
    public set defaultServiceChargeStockItemId(value: Guid | null) {
      this._defaultServiceChargeStockItemId = value === undefined ? null : value;
    }
    private _outletId: Guid | null = null;
    /**
    * Outlet Id. Required in order to perform import. Should be Null only if Zettle is not in use for the tenant.
    */
    public get outletId(): Guid | null { return this._outletId }
    public set outletId(value: Guid | null) {
      this._outletId = value === undefined ? null : value;
    }
    private _ignoreBeforeDate: LocalDate | null = null;
    /**
    * If provided then orders created on Zettle from before this date will be ignored.
    */
    public get ignoreBeforeDate(): LocalDate | null { return this._ignoreBeforeDate }
    public set ignoreBeforeDate(value: LocalDate | null) {
      this._ignoreBeforeDate = value === undefined ? null : value;
    }
    private _stockLevelsStockLocationId: Guid | null = null;
    /**
    * Stock location to be used to calculate stock levels to send to Zettle.
    */
    public get stockLevelsStockLocationId(): Guid | null { return this._stockLevelsStockLocationId }
    public set stockLevelsStockLocationId(value: Guid | null) {
      this._stockLevelsStockLocationId = value === undefined ? null : value;
    }
    private _discountStockItemId: Guid | null = null;
    /**
    * Stock Item Id to be used for order level discounts on Zettle orders.
    */
    public get discountStockItemId(): Guid | null { return this._discountStockItemId }
    public set discountStockItemId(value: Guid | null) {
      this._discountStockItemId = value === undefined ? null : value;
    }
    /**
    * Whether the current integration should run continuously in the background.
    */
    public runInBackground: boolean = false;
    public readOnly: ZettleSettingsReadOnly = new ZettleSettingsReadOnly();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.apiClientId = jsonSerializedObject.apiClientId;
          this.defaultServiceChargeStockItemId = jsonSerializedObject.defaultServiceChargeStockItemId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.defaultServiceChargeStockItemId);
          this.outletId = jsonSerializedObject.outletId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.outletId);
          this.ignoreBeforeDate = jsonSerializedObject.ignoreBeforeDate == null ? null : LocalDate.parse(jsonSerializedObject.ignoreBeforeDate);
          this.stockLevelsStockLocationId = jsonSerializedObject.stockLevelsStockLocationId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.stockLevelsStockLocationId);
          this.discountStockItemId = jsonSerializedObject.discountStockItemId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.discountStockItemId);
          this.runInBackground = jsonSerializedObject.runInBackground;
          this.readOnly = new ZettleSettingsReadOnly(jsonSerializedObject.readOnly);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.apiClientId = this.apiClientId;
        toRet.defaultServiceChargeStockItemId = this.defaultServiceChargeStockItemId == null ? null : this.defaultServiceChargeStockItemId.toString();
        toRet.outletId = this.outletId == null ? null : this.outletId.toString();
        toRet.ignoreBeforeDate = this.ignoreBeforeDate == null ? null : this.ignoreBeforeDate.toString();
        toRet.stockLevelsStockLocationId = this.stockLevelsStockLocationId == null ? null : this.stockLevelsStockLocationId.toString();
        toRet.discountStockItemId = this.discountStockItemId == null ? null : this.discountStockItemId.toString();
        toRet.runInBackground = this.runInBackground;
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "defaultServiceChargeStockItemId", rule: (v: any) => v == null || !v.isEmpty() || "ZettleSettings.DefaultServiceChargeStockItemId cannot be empty"},
            {field: "outletId", rule: (v: any) => v == null || !v.isEmpty() || "ZettleSettings.OutletId cannot be empty"},
            {field: "stockLevelsStockLocationId", rule: (v: any) => v == null || !v.isEmpty() || "ZettleSettings.StockLevelsStockLocationId cannot be empty"},
            {field: "discountStockItemId", rule: (v: any) => v == null || !v.isEmpty() || "ZettleSettings.DiscountStockItemId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
    ];
}


export class ZettleSettingsService extends ServiceBase {

  public async getZettleSettings(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<ZettleSettings> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => new ZettleSettings(response.zettleSettings);;
    return this.makeJsonRequest("zettleSettings/v1/getZettleSettings", req, false, extractor, opts);
  };

  public async saveZettleSettings(zettleSettings: ZettleSettings | null, apiKey: string | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    zettleSettings:zettleSettings == null ? null : zettleSettings?.toJsonSerializedObject() ?? null,
    apiKey:apiKey == null ? null : apiKey,
    };
    const extractor = null;
    return this.makeJsonRequest("zettleSettings/v1/saveZettleSettings", req, true, extractor, opts);
  };
}

export const zettleSettingsService = new ZettleSettingsService();